import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { getNativeHtmlFontSizePx } from '../../utils/nativeDisplayPreferences';
import { useNativeDisplayPreferences } from '../../hooks/useNativeDisplayPreferences';
import { isNativeShell } from '../../utils/distribution';

interface NativeDisplayContextValue {
  density: ReturnType<typeof useNativeDisplayPreferences>['density'];
  setDensity: ReturnType<typeof useNativeDisplayPreferences>['setDensity'];
  isNativeShell: boolean;
}

const NativeDisplayContext = createContext<NativeDisplayContextValue | null>(null);

export function useNativeDisplayContext(): NativeDisplayContextValue {
  const context = useContext(NativeDisplayContext);
  if (!context) {
    return {
      density: 'comfortable',
      setDensity: () => undefined,
      isNativeShell: false,
    };
  }
  return context;
}

interface NativeDisplayProviderProps {
  baseTheme: Theme;
  children: React.ReactNode;
}

const NativeDisplayProvider: React.FC<NativeDisplayProviderProps> = ({ baseTheme, children }) => {
  const preferences = useNativeDisplayPreferences();
  const theme = useMemo(
    () =>
      createTheme(baseTheme, {
        typography: {
          htmlFontSize: getNativeHtmlFontSizePx(preferences.density),
        },
      }),
    [baseTheme, preferences.density],
  );

  return (
    <NativeDisplayContext.Provider value={preferences}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </NativeDisplayContext.Provider>
  );
};

export function AppThemeProvider({
  baseTheme,
  children,
}: {
  baseTheme: Theme;
  children: React.ReactNode;
}) {
  if (isNativeShell()) {
    return <NativeDisplayProvider baseTheme={baseTheme}>{children}</NativeDisplayProvider>;
  }

  return <ThemeProvider theme={baseTheme}>{children}</ThemeProvider>;
}

export default NativeDisplayProvider;
